import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCall } from "@/context/CallContext";
import {
  Phone, VideoCamera, ChatCircleText, UserPlus, AddressBook, X,
} from "@phosphor-icons/react";

const INVITE_TEXT = "Join me on iYeneh Connect — free calls, video & messaging. ";

const sigOf = (list) => list.map((s) => (s.tel || s.phone || s.email || "").toLowerCase()).sort().join("|");

export const IyenehContacts = ({ title = "Your iYeneh friends", smsEnabled = false }) => {
  const call = useCall();
  const navigate = useNavigate();
  const [imported, setImported] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");

  const { data: saved } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: refData } = useQuery({ queryKey: ["referral-link"], queryFn: async () => (await api.get("/referrals/me")).data, staleTime: 300000, retry: false });
  const inviteLink = refData?.link || window.location.origin;

  const sources = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (name, tel, email) => {
      const key = (tel || email || "").toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({ name: name || "", tel: tel || "", email: email || "" });
    };
    (saved?.contacts || []).forEach((c) => push(c.name, c.number, ""));
    imported.forEach((c) => push(c.name, c.tel, c.email));
    return out;
  }, [saved, imported]);

  const sig = sigOf(sources);
  const { data: match, isFetching } = useQuery({
    queryKey: ["contact-match", sig],
    queryFn: async () => (await api.post("/contacts/match", { contacts: sources })).data,
    enabled: sources.length > 0,
    refetchInterval: 20000,
  });
  const members = match?.members || [];
  const others = match?.others || [];

  const addImported = useCallback((rows) => {
    setImported((prev) => {
      const seen = new Set(prev.map((p) => (p.tel || p.email || "").toLowerCase()));
      const add = rows.filter((r) => {
        const k = (r.tel || r.email || "").toLowerCase();
        if (!k || seen.has(k)) return false; seen.add(k); return true;
      });
      return [...prev, ...add];
    });
  }, []);

  const importDevice = async () => {
    if (!("contacts" in navigator) || !navigator.contacts?.select) { setManualOpen(true); return; }
    try {
      const picked = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });
      const rows = (picked || []).map((p) => ({
        name: (p.name && p.name[0]) || "",
        tel: (p.tel && p.tel[0]) || "",
        email: (p.email && p.email[0]) || "",
      })).filter((r) => r.tel || r.email);
      if (rows.length === 0) { toast("No usable contacts selected"); return; }
      addImported(rows);
      toast.success(`Checking ${rows.length} contact${rows.length === 1 ? "" : "s"} on iYeneh…`);
    } catch { setManualOpen(true); }
  };

  const applyManual = () => {
    const rows = manualText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).map((v) => (
      v.includes("@") ? { name: "", tel: "", email: v } : { name: "", tel: v, email: "" }
    ));
    if (rows.length === 0) { toast.error("Paste at least one phone number or email"); return; }
    addImported(rows);
    setManualText(""); setManualOpen(false);
    toast.success(`Checking ${rows.length} contact${rows.length === 1 ? "" : "s"}…`);
  };

  const ring = (m, video) => call?.startCall?.(m.user_id, video);
  const messageMember = (m) => navigate(`/app/text?with=${m.user_id}`);
  const invite = (tel) => {
    const body = encodeURIComponent(`${INVITE_TEXT}${inviteLink}`);
    window.location.href = tel ? `sms:${tel}?body=${body}` : `sms:?body=${body}`;
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="iyeneh-contacts">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</h3>
        <button data-testid="import-device-contacts" onClick={importDevice}
          className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-transform hover:scale-105">
          <AddressBook size={14} weight="bold" /> Find friends
        </button>
      </div>

      {sources.length === 0 && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          Tap <span className="font-semibold text-primary">Find friends</span> to see which of your contacts are on iYeneh.
        </p>
      )}

      {members.length > 0 && (
        <div className="space-y-1" data-testid="iyeneh-members">
          {members.map((m) => (
            <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary">
              <div className="relative">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-primary">
                  {(m.name || "?").charAt(0).toUpperCase()}
                </div>
                <span data-testid={`presence-${m.user_id}`} title={m.online ? "Online" : "Offline"}
                  className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${m.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{m.name}</p>
                <p className={`truncate text-xs ${m.online ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{m.online ? "Online" : "Offline · rings via push"}</p>
              </div>
              <button data-testid={`member-audio-${m.user_id}`} onClick={() => ring(m, false)} title="Audio call"
                className="rounded-full bg-primary/10 p-2 text-primary transition-transform hover:scale-110"><Phone size={16} weight="fill" /></button>
              <button data-testid={`member-video-${m.user_id}`} onClick={() => ring(m, true)} title="Video call"
                className="rounded-full bg-primary/10 p-2 text-primary transition-transform hover:scale-110"><VideoCamera size={16} weight="fill" /></button>
              {smsEnabled && (
                <button data-testid={`member-sms-${m.user_id}`} onClick={() => messageMember(m)} title="Message"
                  className="rounded-full bg-primary/10 p-2 text-primary transition-transform hover:scale-110"><ChatCircleText size={16} weight="fill" /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {others.length > 0 && (
        <div className="mt-4" data-testid="iyeneh-invites">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Not on iYeneh yet</p>
          <div className="space-y-1">
            {others.slice(0, 20).map((o, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-muted-foreground">
                  {(o.name || o.tel || o.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{o.name || o.tel || o.email}</p>
                  <p className="truncate text-xs text-muted-foreground">{o.name ? (o.tel || o.email) : ""}</p>
                </div>
                <button data-testid={`invite-${i}`} onClick={() => invite(o.tel)}
                  className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold transition-colors hover:border-primary hover:text-primary">
                  <UserPlus size={14} weight="bold" /> Invite
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sources.length > 0 && members.length === 0 && others.length === 0 && !isFetching && (
        <p className="py-4 text-center text-sm text-muted-foreground">No matches found in your contacts.</p>
      )}

      {manualOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setManualOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()} data-testid="manual-contacts-modal">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Add contacts</h3>
              <button onClick={() => setManualOpen(false)}><X size={18} /></button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">Your browser can't open the device picker. Paste phone numbers or emails (one per line).</p>
            <textarea data-testid="manual-contacts-input" value={manualText} onChange={(e) => setManualText(e.target.value)} rows={5}
              placeholder={"+12025551234\nmaria@example.com"} className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            <button data-testid="manual-contacts-check" onClick={applyManual} className="mt-3 w-full rounded-full bg-primary py-2.5 font-semibold text-primary-foreground">Check on iYeneh</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IyenehContacts;
